import React, { useMemo, useContext } from "react";
import {
  AppstoreOutlined,
  DatabaseOutlined,
  DotChartOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  RobotOutlined,
  PartitionOutlined,
  ProfileOutlined,
  ProjectOutlined,
  TeamOutlined,
  ToolOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import { Layout, Menu } from "antd";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../services/useTheme";
import { AuthContext } from "../services/authcontext";

const { Sider } = Layout;

const LogoIcon = ({ src, alt }) => (
  <img src={src} alt={alt} className="menu-logo" style={{ width: 20 }} />
);

// Define the static Admin items
const adminItems = [
  { key: "/Users", icon: <TeamOutlined />, label: "Users" },
  { key: "/Activity-Logs", icon: <HistoryOutlined />, label: "Activity Logs" },
];

// Convert menuItems to a function so it can dynamically load dark mode icons
const getMenuItems = (isDark) => [
  { key: "/", label: "Home", icon: <HomeOutlined /> },
  { key: "/instances", label: "Instances", icon: <AppstoreOutlined /> },
  { key: "/projects", label: "Projects", icon: <FolderOpenOutlined /> },
  {
    key: "/skeyenote",
    label: "Skeyenote",
    icon: (
      <LogoIcon
        src={`/img/majesteye-icon-skeye-note-square-white-background-no-label${isDark ? "-white" : ""}.png`}
        alt="Skeyenote"
      />
    ),
    children: [
      { key: "/skeyenote/notes", label: "Notes", icon: <ProfileOutlined /> },
      { key: "/skeyenote/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/skeyecharts",
    label: "Skeyecharts",
    icon: (
      <LogoIcon
        src={`/img/majesteye-icon-skeye-charts-square-white-background-no-label${isDark ? "-white" : ""}.png`}
        alt="Skeyecharts"
      />
    ),
    children: [
      {
        key: "/skeyecharts/dashboards",
        label: "Dashboards",
        icon: <DotChartOutlined />,
      },
      {
        key: "/skeyecharts/datasources",
        label: "Datasources",
        icon: <DatabaseOutlined />,
      },
      { key: "/skeyecharts/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/skeyeingest",
    label: "Skeyeingest",
    icon: (
      <LogoIcon
        src={`/img/majesteye-icon-skeye-ingest-square-white-background-no-label${isDark ? "-white" : ""}.png`}
        alt="Skeyeingest"
      />
    ),
    children: [
      {
        key: "/skeyeingest/pipelines",
        label: "Pipelines",
        icon: <PartitionOutlined />,
      },
      { key: "/skeyeingest/users", label: "Users", icon: <TeamOutlined /> },
      {
        key: "/skeyeingest/executors",
        label: "Executors",
        icon: <ToolOutlined />,
      },
    ],
  },
  {
    key: "/skeyepredict",
    label: "Skeyepredict",
    icon: (
      <LogoIcon
        src={`/img/majesteye-icon-skeye-predict-square-white-background-no-label${isDark ? "-white" : ""}.png`}
        alt="Skeyepredict"
      />
    ),
    children: [
      {
        key: "/skeyepredict/projects",
        label: "Projects",
        icon: <ProjectOutlined />,
      },
      { key: "/skeyepredict/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/skeyecube",
    label: "Skeyecube",
    icon: (
      <LogoIcon
        src={`/img/majesteye-icon-skeye-cube-square-white-background-no-label${isDark ? "-white" : ""}.png`}
        alt="Skeyecube"
      />
    ),
    children: [
      {
        key: "/skeyecube/projects",
        label: "Projects",
        icon: <ProjectOutlined />,
      },
      { key: "/skeyecube/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/skeyeform",
    label: "Skeyeform",
    icon: <LogoIcon src="/img/skeyeform_initial_logo.svg" alt="Skeyeform" />,
    children: [
      { key: "/skeyeform/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/jeyeda",
    label: "Jeyeda",
    icon: <LogoIcon src="/img/Jeyeda_logo_no_text.png" alt="Jeyeda" />,
    children: [
      { key: "/jeyeda/projects", label: "Projects", icon: <ProjectOutlined /> },
      { key: "/jeyeda/users", label: "Users", icon: <TeamOutlined /> },
      {
        key: "/jeyeda/datasources",
        label: "Datasources",
        icon: <DatabaseOutlined />,
      },
    ],
  },
  {
    key: "/fahras",
    label: "Fahras",
    icon: <LogoIcon src={`/img/Fahras_notext.png`} alt="Fahras" />,
    children: [
      { key: "/fahras/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/dghelper",
    label: "DG Helper",
    icon: (
      <LogoIcon
        src={`/img/DGHELPER_nobg${isDark ? "_white" : ""}.png`}
        alt="DG Helper"
      />
    ),
    children: [
      { key: "/dghelper/users", label: "Users", icon: <TeamOutlined /> },
    ],
  },
  {
    key: "/big-data",
    label: "Big Data",
    icon: (
      <LogoIcon
        src={`/img/bigdata_icon${isDark ? "_white" : ""}.png`}
        alt="Big Data"
      />
    ),
    children: [
      {
        key: "/big-data/hadoop",
        label: "Hadoop",
        icon: (
          <LogoIcon
            src={`/img/hadoop-logo-no-text${isDark ? "-white" : ""}.png`}
            alt="Hadoop"
          />
        ),
      },
      {
        key: "/big-data/hive",
        label: "Hive",
        icon: <LogoIcon src={`/img/hive.svg`} alt="Hive" />,
      },
    ],
  },
  /* { key: "/skeyestudio", label: "SkeyeStudio", icon: <RobotOutlined /> }, */
];

function Sidebar({
  collapsed,
  currentPath,
  onCollapse,
  onBreakpoint,
  onNavigate,
}) {
  const navigate = useNavigate();

  // Read theme securely from localStorage
  const isDarkTheme = useTheme();
  const { user } = useContext(AuthContext);

  // 1. Logic to filter items based on Role and Activated Modules
  const authorizedMenuItems = useMemo(() => {
    if (!user) return [];

    const userRole = user.roles?.[0]?.name;
    const activatedModules =
      user.activatedModules?.map((m) => m.label.toLowerCase()) || [];

    // If Admin, they only see Admin Items + Home
    if (userRole === "ROLE_ADMIN") {
      return [...adminItems];
    }

    // Pass isDarkTheme dynamically generated items
    const currentMenuItems = getMenuItems(isDarkTheme);

    // For other roles, reduce/filter menu items and their children
    return currentMenuItems.reduce((filteredMenu, item) => {
      // 1. Always include standard utility menus
      if (["/", "/instances", "/projects", "/skeyestudio"].includes(item.key)) {
        filteredMenu.push(item);
        return filteredMenu;
      }

      // 2. Safely filter "Big Data" parent and its sub-items
      if (item.label === "Big Data") {
        const bigDataSubMenus = item.children.filter((child) =>
          activatedModules.includes(child.label.toLowerCase()),
        );

        // If they have at least one big data module, include the parent + authorized child modules
        if (bigDataSubMenus.length > 0) {
          filteredMenu.push({ ...item, children: bigDataSubMenus });
        }
        return filteredMenu;
      }

      // 3. For all other Skeye modules, verify the parent item label
      // Allow mapping special UI labels to backend permissions
      let targetLabel = item.label.toLowerCase();
      if (item.label === "Skeyecharts") targetLabel = "skeyechart";
      if (item.label === "DG Helper") targetLabel = "dghelper";

      if (activatedModules.includes(targetLabel)) {
        filteredMenu.push(item);
      }

      return filteredMenu;
    }, []);
  }, [isDarkTheme]);

  // 2. Original menu helpers updated to use the filtered list
  function mapMenuItemsForCollapsed(items, collapsed, isRoot = true) {
    return items.map((item) => ({
      ...item,
      // Hide the text label for all root items when collapsed
      label: collapsed && isRoot ? null : item.label,

      // Pass the label to title so Ant Design uses it for the hover Tooltip
      title: item.label,

      children: item.children
        ? mapMenuItemsForCollapsed(item.children, collapsed, false)
        : undefined,
    }));
  }

  function findMenuState(items, pathname, parentKeys = []) {
    let result = { selectedKey: "", openKeys: [] };

    items.forEach((item) => {
      // Check if this item is a match
      if (pathname === item.key || pathname.startsWith(`${item.key}/`)) {
        if (item.key.length > result.selectedKey.length) {
          result = { selectedKey: item.key, openKeys: parentKeys };
        }
      }

      // Recursively check children
      if (item.children) {
        const childMatch = findMenuState(item.children, pathname, [
          ...parentKeys,
          item.key,
        ]);
        if (
          childMatch.selectedKey &&
          childMatch.selectedKey.length > result.selectedKey.length
        ) {
          result = childMatch;
        }
      }
    });

    return result;
  }

  const menuState = useMemo(() => {
    const state = findMenuState(authorizedMenuItems, currentPath);
    // Default to '/' if nothing matches
    return state.selectedKey ? state : { selectedKey: "/", openKeys: [] };
  }, [currentPath, authorizedMenuItems]);

  const visibleMenuItems = useMemo(
    () => mapMenuItemsForCollapsed(authorizedMenuItems, collapsed),
    [collapsed, authorizedMenuItems],
  );

  const [openKeys, setOpenKeys] = React.useState(menuState.openKeys);

  React.useEffect(() => {
    // Merge new openKeys from the URL with existing open keys so we NEVER force-close a folder the user opened
    setOpenKeys((prev) =>
      Array.from(new Set([...prev, ...menuState.openKeys])),
    );
  }, [menuState.openKeys]);

  return (
    <Sider
      collapsible
      trigger={null}
      width={250}
      collapsed={collapsed}
      collapsedWidth={80}
      className="app-sider"
      onCollapse={onCollapse}
      onBreakpoint={onBreakpoint}
    >
      <div className="app-logo-wrap">
        <img
          src={
            collapsed
              ? `/img/majesteye-icon-skeye-gate${isDarkTheme ? "-white" : ""}.svg`
              : `/img/majesteye-icon-skeye-gate-horizontal-white-background${isDarkTheme ? "-white" : ""}.svg`
          }
          alt="SkeyeGate"
          className={
            collapsed ? "app-main-logo-collapsed" : "app-main-logo-expanded"
          }
        />
      </div>

      <Menu
        theme="light"
        mode="inline"
        selectedKeys={[menuState.selectedKey]}
        openKeys={openKeys}
        onOpenChange={(keys) => setOpenKeys(keys)}
        items={visibleMenuItems}
        onClick={({ key }) => {
          navigate(key);
          if (onNavigate) onNavigate();
        }}
        className="side-menu"
      />
    </Sider>
  );
}

export default Sidebar;
